import { useCallback, useState } from 'react';
import type { TokenUsageSnapshot, TokenUsageSummary } from '../types';

export interface UseTokenUsageResult {
  /** Accumulated token usage for the current session, or null before any response. */
  summary: TokenUsageSummary | null;
  /** Fold one `response.done` token-usage snapshot into the running summary. */
  record: (snapshot: TokenUsageSnapshot) => void;
  /** Clear the accumulated usage (new session). */
  reset: () => void;
}

/**
 * Accumulates the per-response LLM token-usage snapshots the backend relays on
 * `response.done` into a running session summary for the DEV token readout.
 *
 * Unlike useConversationTranscript (ref-based, no re-render), this uses state so
 * the readout updates live as each response completes. Billed totals are sums
 * across responses; peakContextInputTokens tracks the latest response's input
 * count (context grows monotonically within an Azure session, so the last input
 * is the peak — matching the backend summarize_token_usage).
 */
export const useTokenUsage = (): UseTokenUsageResult => {
  const [summary, setSummary] = useState<TokenUsageSummary | null>(null);

  const record = useCallback((snapshot: TokenUsageSnapshot) => {
    setSummary((prev) => ({
      billedTotalTokens: (prev?.billedTotalTokens ?? 0) + (snapshot.total_tokens ?? 0),
      billedInputTokens: (prev?.billedInputTokens ?? 0) + (snapshot.input_tokens ?? 0),
      billedOutputTokens: (prev?.billedOutputTokens ?? 0) + (snapshot.output_tokens ?? 0),
      billedReasoningTokens:
        (prev?.billedReasoningTokens ?? 0) + (snapshot.output_reasoning_tokens ?? 0),
      peakContextInputTokens: snapshot.input_tokens ?? 0,
      responseCount: (prev?.responseCount ?? 0) + 1,
    }));
  }, []);

  const reset = useCallback(() => setSummary(null), []);

  return { summary, record, reset };
};
